import type { AgentIdentity } from "../agent.ts";

export interface WorkPackage {
  /** 1-based number: WP-n. */
  n: number;
  title: string;
  /** The spec section it comes from, e.g. "Spec §2.1". */
  spec: string;
  /** Claimed ground. */
  ground: readonly string[];
  /** Agent index (join order). */
  agent: number;
  /** Work packages that must land first. */
  after: readonly number[];
}

export const wpLabel = (n: number): string => `WP-${n}`;

export interface PlannedOverlap {
  path: string;
  /** Work packages claiming the path, in landing order. */
  sequence: readonly number[];
}

/** Depth in the dependency graph: 0 starts now. */
export function depth(packages: readonly WorkPackage[], n: number, seen = new Set<number>()): number {
  const wp = packages.find((p) => p.n === n);
  if (!wp || seen.has(n)) return 0;
  seen.add(n);
  return wp.after.length === 0 ? 0 : 1 + Math.max(...wp.after.map((a) => depth(packages, a, new Set(seen))));
}

/** Paths claimed by more than one package, each with its landing order (dependency depth, then number). */
export function plannedOverlaps(packages: readonly WorkPackage[]): PlannedOverlap[] {
  const claims = new Map<string, number[]>();
  for (const p of packages) for (const g of p.ground) claims.set(g, [...(claims.get(g) ?? []), p.n]);
  return [...claims]
    .filter(([, wps]) => wps.length > 1)
    .map(([path, wps]) => ({ path, sequence: [...wps].sort((a, b) => depth(packages, a) - depth(packages, b) || a - b) }));
}

/** Paths of a package that overlap another. */
export function overlappingGround(packages: readonly WorkPackage[], wp: WorkPackage): ReadonlySet<string> {
  return new Set(plannedOverlaps(packages).filter((o) => o.sequence.includes(wp.n)).map((o) => o.path));
}

/** "Starts now", or "After WP-2" for the dependency that lands last. */
export function orderLabel(packages: readonly WorkPackage[], wp: WorkPackage): string {
  if (wp.after.length === 0) return "Starts now";
  const last = [...wp.after].sort((a, b) => depth(packages, b) - depth(packages, a) || b - a)[0];
  return `After ${wpLabel(last ?? wp.after[0] ?? 0)}`;
}

/** Drag reassign: hand a package to another agent. Returns a new list; the two agents keep distinct ground. */
export function reassign(packages: readonly WorkPackage[], n: number, toAgent: number): WorkPackage[] {
  return packages.map((p) => (p.n === n ? { ...p, agent: toAgent } : p));
}

export function agentOf(wp: WorkPackage, roster: readonly AgentIdentity[]): AgentIdentity | undefined {
  return roster.find((a) => a.index === wp.agent);
}
